// Read-only display card for a saved AND-group. Shows field, comparator, and value per OR condition.
import React, { useMemo } from "react";

import { faTimes } from "@fortawesome/pro-solid-svg-icons";

import { ButtonIcon, Chip, Text } from "@performyard/py-components";
import { DEFAULT_TIMEZONE } from "@performyard/utils-isomorphic";

import { EmployeeName } from "./EmployeeName";

import type { IEmployeeField, QueryCondition } from "./types";

import { useGroupsInternalApi } from "../../api/hooks";
import { useCompanySettings } from "../../api/hooks/defs/company";

import { getComparatorLabel, getDisplayValue, getFieldName, getPersonOid } from "./utils";

type ConditionCardProps = {
	conditions: QueryCondition[];
	employeeFields: IEmployeeField[];
	onRemove: () => void;
	onClick: () => void;
	isSelected: boolean;
};

export const ConditionCard = ({ conditions, employeeFields, onRemove, onClick, isSelected }: ConditionCardProps) => {
	const { data: groups } = useGroupsInternalApi();
	const { data: companySettings } = useCompanySettings();
	const companyTimezone = companySettings?.default_timezone ?? DEFAULT_TIMEZONE;

	const displayNames = useMemo(() => {
		const names: Record<string, string> = {};
		for (const group of groups ?? []) {
			names[group.id] = group.name;
		}
		return names;
	}, [groups]);

	return (
		<div
			onClick={onClick}
			className={`cursor-pointer rounded border border-solid p-4 ${
				isSelected ? "border-primary-5" : "border-neutral-3"
			}`}
		>
			<div className="flex items-center justify-between">
				<Text size="caption" weight="semi-bold" color="neutral-9">
					Employee Field
				</Text>
				<ButtonIcon
					color="neutral"
					onClick={(e) => {
						e.stopPropagation();
						onRemove();
					}}
					icon={faTimes}
					size={16}
					aria-label="Remove condition"
				/>
			</div>

			<div className="mt-2 flex flex-col gap-2">
				{conditions.map((condition, index) => {
					const fieldName = getFieldName(condition.field, employeeFields);
					const comparatorLabel = getComparatorLabel(condition.comparator, condition.value);
					const personOid = getPersonOid(condition, employeeFields);
					const displayValue = getDisplayValue(condition, employeeFields, displayNames, companyTimezone);

					return (
						<React.Fragment key={index}>
							<div className="flex w-fit items-center gap-2 rounded-full bg-neutral-1 px-4 py-2">
								<Text size="caption" weight="semi-bold" color="neutral-9">
									{fieldName}
								</Text>
								<div className="rounded-full bg-neutral-0 px-2 py-1">
									<Text size="caption" weight="semi-bold" color="neutral-9">
										{comparatorLabel.toUpperCase()}
									</Text>
								</div>
								{personOid ? (
									<Text size="caption" weight="semi-bold" color="neutral-9">
										<EmployeeName employeeId={personOid} />
									</Text>
								) : (
									displayValue && (
										<Text size="caption" weight="semi-bold" color="neutral-9">
											{displayValue}
										</Text>
									)
								)}
							</div>
							{index < conditions.length - 1 && (
								<Chip label="OR" color="neutral" size={24} dangerouslySetClassName="!w-[48px]" />
							)}
						</React.Fragment>
					);
				})}
			</div>
		</div>
	);
};
